import {
  BagModalButtons,
  BagModalProduct,
} from "@/components/tactsuit/components/buy-tactsuit/bag-modal-shell";
import { GePrice } from "@/v2-components/GePrice";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { useProductPrice } from "@/hooks/useProductPrice";
import { useCurrency } from "@/hooks/useCurrency";
import { useLocale } from "@/store/locale-store";
import CountryUtils from "@/utils/CountryUtils";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";

/**
 * 담기 완료 확인의 **본문** (#5355 → #5366).
 *
 * 시안: 데스크톱 [29459:226902](EN) / 29417:91969(KR) ·
 *       모바일 [29459:226928](EN) / 29443:37732(KR).
 *
 * ## 이 파일이 「본문만」인 이유 — 표면이 둘로 갈렸다 (#5366)
 *
 * #5355 때는 표면이 **중앙 다이얼로그 / 바텀 시트** 였고 `BagModalShell` 이 둘 다 그렸다.
 * 기획이 정정되면서(#5366) 데스크톱이 **GNB 장바구니 아이콘 아래 앵커 팝(딤 없음)** 이
 * 됐다 — 셸의 중앙 다이얼로그를 못 쓴다. 그래서 껍데기는 `added-to-bag-pop.tsx` 가 표면별로
 * 정하고, 이 파일은 **두 표면이 공유하는 본문**만 갖는다.
 *
 * 두 표면의 본문 차이는 썸네일 크기(데스크톱 80 / 모바일 64)와 버튼 배치(나란히 / 세로)뿐이라
 * 반응형 클래스로 처리한다.
 *
 * ## ★어디서 뜨나 — **액세서리 전용**이다 (#5366)
 *
 * 메인 제품(`AddToCartResult.Success`)은 종전대로 `/cart` 로 **이동**한다. #5359 에서 그 자리를
 * 이 확인 모달로 바꿨는데 기획이 되돌렸다(이슈 #5366: *"메인제품 … 장바구니 페이지로 이동했는데
 * 그건 유지"*). 이 본문이 뜨는 경로는 **`useAddItemToCart` 의 무음 성공 분기** 하나다.
 *
 * ## 요약줄 — 배송 줄은 **무료가 확정될 때만**
 *
 * EN 시안은 1줄(`1 item in cart · Subtotal $0,000`), KR 시안은 2줄(소계 + 무료배송)이다.
 * 로케일로 가르지 않고 **무료배송이 확정되는지**로 가른다(2026-08-25 결정).
 *
 * 판정 근거는 추측이 아니라 코드다 — `ShippingApi.calculateShippingPriceBySku` 는
 * `countryCode === "KR"` 이면 **네트워크를 타지 않고 `price: 0` 을 즉시 반환**한다.
 * 그래서 KR 은 조회 없이 무료가 확정되고, 비-KR 은 조회해야 알 수 있다.
 * → **비-KR 에서는 배송 줄을 그리지 않는다.**
 */
export function AddedToBagBody({
  skus,
  onClose,
  onViewCart,
}: {
  skus: string[];
  onClose: () => void;
  onViewCart: () => void;
}) {
  const { t } = useTrans(["gnb"]);
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const { items, totalCount, displayTotalOriginalPrice, isPending } =
    useCartWithProducts();

  // 방금 담은 항목들(담은 순서). 같은 SKU 가 여러 줄로 쪼개지지 않으므로 SKU 마다 첫 일치가 그 항목이다.
  // 카트에 아직 없는 SKU(동기화 전 경계)는 건너뛴다 — 요약·버튼은 그대로 둔다.
  const added = skus.flatMap((sku) => {
    const item = items.find((i) => i.sku === sku);
    return item ? [item] : [];
  });

  return (
    <>
      {/* Contents — 시안 `gap-20` */}
      <div className="flex flex-col gap-5">
        {/* 여러 개(옵션 다이얼로그로 색 여러 개)면 목록이 길어진다 — 팝이 화면을 넘지 않게 목록만 스크롤. */}
        {added.length > 0 && (
          <div
            className={cn(
              "flex flex-col gap-3",
              added.length > 1 && "max-h-[50vh] overflow-y-auto",
            )}
          >
            {added.map((item) => (
              <AddedItemCard key={item.sku} item={item} />
            ))}
          </div>
        )}

        {/* Summary — 시안은 개수·소계를 **한 줄에** 둔다(`1 item in cart · Subtotal $0,000`).
            금액만 `GePrice` 로 감싼다 — 문장 전체를 감싸면 GE 가 통화 변환 대상으로 오인한다. */}
        <div className="flex flex-col gap-1 text-base tracking-[0.25px] text-coolgray-60">
          <p className="flex flex-wrap items-center gap-1">
            <span>{t("{{count}} items in bag", { count: totalCount })}</span>
            <span aria-hidden>·</span>
            <span>{t("Subtotal")}</span>
            {isPending ? (
              <span>—</span>
            ) : (
              <GePrice
                amount={displayTotalOriginalPrice}
                currency={currency}
                countryCode={countryCode}
              />
            )}
          </p>
          {/* ★배송 줄은 **무료가 확정될 때만**. 근거는 `ShippingApi.calculateShippingPriceBySku`
              가 KR 을 네트워크 없이 `price: 0` 으로 단락시키는 것 — 비-KR 은 조회해야 알 수
              있으므로 그리지 않는다(위 컴포넌트 주석). */}
          {CountryUtils.isKR(countryCode) && (
            <p className="text-blue-60">{t("Free Shipping")}</p>
          )}
        </div>
      </div>

      {/* 버튼 두 개도 공용 — 목적지만 이 모달이 정한다
          (`Keep Shopping` = 닫기 → PDP 에 머무름 · `View Bag` = 카트 이동). */}
      <BagModalButtons onKeepShopping={onClose} onViewCart={onViewCart} />
    </>
  );
}

type CartItem = ReturnType<typeof useCartWithProducts>["items"][number];

/**
 * 방금 담은 항목 하나의 카드. 항목마다 가격 훅(`useProductPrice`)이 필요해서 컴포넌트로 뺐다
 * (여러 SKU 를 한 번에 보여 주게 되면서 — `useAddedToBagStore` 의 「SKU 목록」).
 *
 * 카드 껍데기·썸네일·옵션 라벨은 공용(`BagModalProduct`) — 시안이 #5356 과 같다.
 * 썸네일만 이 모달이 데스크톱에서 80px 이다(번들 병합은 64px 고정).
 */
function AddedItemCard({ item }: { item: CartItem }) {
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const { getProductPrice } = useCartWithProducts();
  const price = getProductPrice(item);
  const { displaySalePrice, displayOriginalPrice, displayDiscountAmount } =
    useProductPrice(price);

  return (
    <BagModalProduct
      thumbnail={
        item.skuOptions?.find((opt) => opt.sku === item.sku)?.thumbnail ||
        item.thumbnail
      }
      name={item.name}
      options={item.options}
      thumbnailClassName="laptop:size-20"
      nameClassName="font-500 tracking-[0.1px]"
    >
      {!!price && (
        /* 판매가(볼드) + 할인 시 정가(줄긋기). `cart-item` 과 **같은 형태·같은
           `basketAttr`** 를 쓴다 — Global-E 스펙(2026-06-16)이 현재가를
           `producttotalprice`, 원가를 `productsubtotalprice` 로 읽는다.
           두 곳이 갈리면 GE 가 카트와 이 모달에서 다른 값을 집계한다. */
        <div className="flex flex-wrap items-center gap-1">
          <GePrice
            amount={displaySalePrice * item.quantity}
            currency={currency}
            countryCode={countryCode}
            basketAttr="producttotalprice"
            className="text-base font-600 tracking-[0.25px] text-coolgray-90"
          />
          {!!displayDiscountAmount && (
            <GePrice
              amount={displayOriginalPrice * item.quantity}
              currency={currency}
              countryCode={countryCode}
              basketAttr="productsubtotalprice"
              className="text-sm text-coolgray-50 line-through"
            />
          )}
        </div>
      )}
    </BagModalProduct>
  );
}
